import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  hallOfFame,
  ownerRecords,
  recordBook,
  HISTORY_SEASONS,
  LEAGUE,
  type HistorySeason,
} from '@ff/core';
import { useSeasonSelect } from '../lib/seasonSelect.tsx';
import { Avatar } from '../components/Avatar.tsx';
import { ordinal, record } from '../lib/format.ts';

/**
 * The record book: the whole league in one place. Champions, all-time ledgers,
 * and every final standing on file — the receipts that settle the offseason
 * arguments.
 *
 * A league that has just stood this app up has no history to show, and the
 * honest answer is to say so rather than render four empty sections under copy
 * claiming a decade of them.
 */
export function History() {
  const champs = hallOfFame();
  const records = ownerRecords();
  const book = recordBook();
  const years = HISTORY_SEASONS.map((s) => s.year);
  const since = years.length ? Math.min(...years) : null;

  if (HISTORY_SEASONS.length === 0) {
    return (
      <div className="space-y-4">
        <div>
          <p className="eyebrow">{LEAGUE.name}</p>
          <h1 className="mt-1 font-display text-4xl font-black uppercase tracking-tight">
            History
          </h1>
        </div>
        <p className="max-w-prose font-editorial text-sm text-chalk-dim">
          Nothing on file yet. Seasons finished before this app existed are
          transcribed by hand into the league config; once they are, the
          champions, the all-time ledger and every final standing show up here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      <div>
        <p className="eyebrow">
          {LEAGUE.name}
          {since !== null && ` · since ${since}`}
        </p>
        <h1 className="mt-1 font-display text-4xl font-black uppercase tracking-tight">
          History
        </h1>
        <p className="mt-2 max-w-prose font-editorial text-sm text-chalk-dim">
          {years.length} {years.length === 1 ? 'season' : 'seasons'},{' '}
          {champs.length} {champs.length === 1 ? 'champion' : 'champions'}, and
          every last-place finish on the record. Members have come and gone; the
          receipts haven't.
        </p>
      </div>

      {/* The record book: headline superlatives. */}
      <section className="space-y-3">
        <h2 className="eyebrow">The record book</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {book.map((l) => (
            <div key={l.label} className="card p-4">
              <p className="font-mono text-[0.65rem] uppercase tracking-wider text-chalk-dim">
                {l.label}
              </p>
              <p className="mt-1 font-display text-xl font-bold">{l.owner}</p>
              <p className="font-mono text-xs text-brass">{l.detail}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Hall of Fame: champions by year. */}
      <section className="space-y-3">
        <h2 className="eyebrow">Hall of Fame</h2>
        <ol className="space-y-2">
          {champs.map((c) => (
            <li key={c.year} className="card flex items-center gap-4 p-3">
              <span className="w-12 shrink-0 font-mono text-lg font-bold tabular text-brass">
                {c.year}
              </span>
              <Avatar name={c.owner} size={40} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-display text-lg font-bold">
                  {c.owner} <span aria-hidden>🏆</span>
                </p>
                <p className="truncate font-editorial text-sm italic text-chalk-dim">
                  {c.team}
                </p>
              </div>
              {c.runnerUp && (
                <div className="hidden text-right sm:block">
                  <p className="font-mono text-[0.6rem] uppercase tracking-wider text-chalk-dim">
                    runner-up
                  </p>
                  <p className="font-mono text-xs text-chalk-dim">{c.runnerUp}</p>
                </div>
              )}
            </li>
          ))}
        </ol>
      </section>

      {/* All-time ledger. */}
      <section className="space-y-3">
        <h2 className="eyebrow">All-time</h2>
        <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <table className="w-full min-w-[560px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-felt-line text-left font-mono text-[0.6rem] uppercase tracking-wider text-chalk-dim">
                <th className="py-2 pr-3 font-normal">Manager</th>
                <th className="py-2 pr-3 text-right font-normal">Yrs</th>
                <th className="py-2 pr-3 text-right font-normal">Record</th>
                <th className="py-2 pr-3 text-right font-normal">Win%</th>
                <th className="py-2 pr-3 text-right font-normal">Titles</th>
                <th className="py-2 pr-3 text-right font-normal">Top 3</th>
                <th className="py-2 pr-3 text-right font-normal">Avg</th>
                <th className="py-2 text-right font-normal">Points</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.owner} className="border-b border-felt-line/50">
                  <td className="py-2 pr-3">
                    <span className="flex items-center gap-2">
                      <Avatar name={r.owner} size={24} />
                      <span className="font-display font-bold">{r.owner}</span>
                      {r.titles > 0 && (
                        <span className="font-mono text-[0.6rem] text-brass">
                          {'★'.repeat(r.titles)}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-right font-mono tabular text-chalk-dim">
                    {r.seasons}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono tabular">
                    {record(r.wins, r.losses, r.ties)}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono tabular">
                    {(r.winPct * 100).toFixed(1)}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono tabular text-brass">
                    {r.titles || '·'}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono tabular text-chalk-dim">
                    {r.podiums || '·'}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono tabular text-chalk-dim">
                    {r.avgFinish.toFixed(1)}
                  </td>
                  <td className="py-2 text-right font-mono tabular text-chalk-dim">
                    {r.pointsFor.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="font-mono text-[0.65rem] text-chalk-dim">
          Win% counts a tie as half a win. Avg = average final placement.
        </p>
      </section>

      {/* Season by season. */}
      <section className="space-y-3">
        <h2 className="eyebrow">Season by season</h2>
        <div className="space-y-2">
          {[...HISTORY_SEASONS]
            .sort((a, b) => b.year - a.year)
            .map((s) => (
              <SeasonRow key={s.year} season={s} />
            ))}
        </div>
      </section>
    </div>
  );
}

function SeasonRow({ season }: { season: HistorySeason }) {
  const [open, setOpen] = useState(false);
  const { seasons, setSelected } = useSeasonSelect();
  const navigate = useNavigate();
  const champ = season.standings.find((t) => t.rank === 1);
  const hasBoard = seasons?.includes(season.year) ?? false;

  const openBoard = () => {
    setSelected(season.year);
    navigate('/book');
  };

  return (
    <div className="card overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-4 p-3 text-left hover:bg-felt"
      >
        <span className="w-12 shrink-0 font-mono text-lg font-bold tabular text-brass">
          {season.year}
        </span>
        <span className="min-w-0 flex-1">
          <span className="font-mono text-[0.6rem] uppercase tracking-wider text-chalk-dim">
            champion
          </span>
          <span className="block truncate font-display font-bold">
            {champ?.owner} <span aria-hidden>🏆</span>
          </span>
        </span>
        <span className="font-mono text-xs text-chalk-dim">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="border-t border-felt-line">
          {hasBoard && (
            <div className="flex items-center justify-between gap-3 border-b border-felt-line px-3 py-2">
              <span className="font-mono text-[0.7rem] text-chalk-dim">
                We have the full money board for {season.year}.
              </span>
              <button
                onClick={openBoard}
                className="shrink-0 rounded-md border border-brass/50 px-2.5 py-1 font-mono text-[0.65rem] uppercase tracking-wider text-brass hover:bg-brass/15"
              >
                Open money board →
              </button>
            </div>
          )}
          <ol>
            {season.standings.map((t) => (
              <li
                key={t.rank}
                className="flex items-center gap-3 px-3 py-1.5 font-mono text-sm odd:bg-felt/40"
              >
                <span className="w-8 shrink-0 tabular text-chalk-dim">{ordinal(t.rank)}</span>
                <span className="min-w-0 flex-1 truncate font-display font-bold text-chalk">
                  {t.owner}
                </span>
                <span className="hidden truncate font-editorial text-xs italic text-chalk-dim sm:block sm:w-48">
                  {t.team}
                </span>
                <span className="shrink-0 tabular text-chalk-dim">
                  {record(t.wins, t.losses, t.ties)}
                </span>
                <span className="hidden shrink-0 tabular text-chalk-dim sm:block">
                  {t.pf.toFixed(0)} pf
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
